import type { FloatingNavMode } from '@/store/ui-preferences.store'
import { useCallback, useEffect, useRef, useState } from 'react'

/** Hover expansion is temporary; only the selected navigation mode persists. */
export function useFloatingNavExpansion(
  mode: FloatingNavMode,
  onNavigationLeave?: () => void,
) {
  const [hoverExpanded, setHoverExpanded] = useState(false)
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const onNavigationLeaveRef = useRef(onNavigationLeave)

  useEffect(() => {
    onNavigationLeaveRef.current = onNavigationLeave
  }, [onNavigationLeave])

  const cancelPendingOpen = useCallback(() => {
    if (openTimer.current !== null) {
      clearTimeout(openTimer.current)
      openTimer.current = null
    }
  }, [])

  const cancelPendingClose = useCallback(() => {
    if (closeTimer.current !== null) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }, [])

  const collapse = useCallback(() => {
    setHoverExpanded(false)
    cancelPendingOpen()
    cancelPendingClose()
  }, [cancelPendingOpen, cancelPendingClose])

  useEffect(() => {
    collapse()
    return () => {
      cancelPendingOpen()
      cancelPendingClose()
    }
  }, [mode, collapse, cancelPendingOpen, cancelPendingClose])

  const handlePointerLeave = useCallback(() => {
    cancelPendingOpen()
    cancelPendingClose()
    // Sidebar and flyout are siblings with rounded edges. Bridge the gap
    // before closing both together; entering either surface cancels this.
    closeTimer.current = setTimeout(() => {
      closeTimer.current = null
      setHoverExpanded(false)
      onNavigationLeaveRef.current?.()
    }, 180)
  }, [cancelPendingOpen, cancelPendingClose])

  const handlePointerEnter = useCallback(() => {
    cancelPendingClose()
    if (mode !== 'hybrid' || hoverExpanded || openTimer.current !== null) return
    openTimer.current = setTimeout(() => {
      openTimer.current = null
      setHoverExpanded(true)
    }, 300)
  }, [mode, hoverExpanded, cancelPendingClose])

  return {
    isExpanded: mode === 'open' || (mode === 'hybrid' && hoverExpanded),
    handlePointerEnter,
    handlePointerLeave,
    cancelPendingClose,
    collapse,
  }
}
